import React, { useEffect, useState } from 'react';
import { ChevronDown, Filter, RotateCcw } from 'lucide-react';
import { DealFilterPanel, buildExploreBody, DEFAULT_FILTERS, useFilterOptions, filterSummary } from './DealFilterPanel';
import { FundThemeFilter } from './FundThemeFilter';

const KEY = 'infra.screenFilters';

// One filter set for every Infra-Fonds screening (fund profile, angles, single deal) — persisted per browser.
export function useInfraScreenFilters() {
  const [filters, setFilters] = useState(() => {
    try { return { ...DEFAULT_FILTERS, ...(JSON.parse(window.localStorage.getItem(KEY) || 'null') || {}) }; } catch { return DEFAULT_FILTERS; }
  });
  useEffect(() => { try { window.localStorage.setItem(KEY, JSON.stringify(filters)); } catch { /* ignore */ } }, [filters]);
  const { sectorConfig, ownerOptions } = useFilterOptions();
  const updateFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const reset = () => setFilters(DEFAULT_FILTERS);
  const active = filterSummary(filters, ownerOptions);
  // Panel always starts collapsed and folds away again once a screening is started.
  const [open, setOpen] = useState(false);
  const body = () => { setOpen(false); return buildExploreBody(filters); };
  return { filters, setFilters, updateFilter, reset, active, body, sectorConfig, ownerOptions, open, setOpen };
}

// Fund-specific theme selection (not persisted — themes differ from fund to fund).
export function useThemeSelection(options) {
  const [selected, setSelected] = useState([]);
  const valid = new Set((options || []).map(o => o.id));
  const current = selected.filter(id => valid.has(id));
  const toggle = (id) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const clear = () => setSelected([]);
  const selectAll = (ids) => setSelected(prev => Array.from(new Set([...prev, ...ids])));
  const labels = current.map(id => (options || []).find(o => o.id === id)?.label).filter(Boolean);
  return { options: options || [], selected: current, toggle, clear, selectAll, labels };
}

export function InfraScreenFilters({ f, prefix = 'infra', accent = 'amber', appliesTo = 'alle Screenings in Infra-Fonds', themes, fundId }) {
  const { open, setOpen } = f;
  const toggle = () => setOpen(v => !v);
  const tone = accent === 'sky' ? 'text-sky-300 bg-sky-500/10 border-sky-500/30' : 'text-amber-300 bg-amber-500/10 border-amber-500/30';
  const themeChips = (themes?.labels || []).map(l => `Thema: ${l}`);
  const chips = [...themeChips, ...f.active];
  const resetAll = () => { f.reset(); themes?.clear?.(); };
  return (
    <div className="bg-card border border-border rounded-sm p-3 space-y-2" data-testid={`${prefix}-screen-filters`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={toggle} data-testid={`${prefix}-screen-filters-toggle`}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground hover:text-white">
          <Filter className="w-3.5 h-3.5" /> Filter für die Deal-Suche{themes?.options?.length ? ' & Themen' : ''}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          {chips.length > 0 && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${tone}`} data-testid={`${prefix}-screen-filters-count`}>{chips.length} aktiv</span>
          )}
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-muted-foreground/70">
            Gilt für {appliesTo}. Ohne Stage-Filter: nur Succession Possible + OLTA TAM.
          </span>
          {chips.length > 0 && (
            <button onClick={resetAll} data-testid={`${prefix}-screen-filters-reset`}
              className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <RotateCcw className="w-3 h-3" /> Zurücksetzen
            </button>
          )}
        </div>
      </div>
      {open && (
        <>
          {themes && <FundThemeFilter options={themes.options} selected={themes.selected} toggle={themes.toggle} clear={themes.clear} selectAll={themes.selectAll} prefix={prefix} fundId={fundId} />}
          <DealFilterPanel filters={f.filters} updateFilter={f.updateFilter} setFilters={f.setFilters}
            sectorConfig={f.sectorConfig} ownerOptions={f.ownerOptions} accent={accent === 'sky' ? 'sky' : 'amber'}
            searchPlaceholder="Name, Code-Name, Beschreibung, Produkte… (schränkt den Pool fürs Screening ein)" />
        </>
      )}
      {!open && chips.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`${prefix}-screen-filter-chips`}>
          {chips.map(c => <span key={c} className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${tone}`}>{c}</span>)}
        </div>
      )}
    </div>
  );
}
